import clsx from "clsx";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  defaultPhoneCountry,
  formatPhoneValue,
  PHONE_COUNTRIES,
  splitPhoneValue,
  type PhoneCountry,
} from "@/lib/phoneCountries";

type Props = {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  missing?: boolean;
  className?: string;
  autoComplete?: string;
};

export function PhoneInput({
  value,
  onChange,
  required,
  missing = false,
  className,
  autoComplete = "tel",
}: Props) {
  const listId = useId();
  const [country, setCountry] = useState<PhoneCountry>(() => defaultPhoneCountry());
  const [nationalNumber, setNationalNumber] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const sortedCountries = useMemo(
    () => [...PHONE_COUNTRIES].sort((a, b) => a.name.localeCompare(b.name, "de")),
    [],
  );

  useEffect(() => {
    const parsed = splitPhoneValue(value, country);
    setCountry(parsed.country);
    setNationalNumber(parsed.nationalNumber);
    // Nur bei externer Wertänderung synchronisieren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!pickerOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [pickerOpen]);

  function emit(nextCountry: PhoneCountry, nextNational: string) {
    onChange(formatPhoneValue(nextCountry, nextNational));
  }

  function selectCountry(next: PhoneCountry) {
    setCountry(next);
    setPickerOpen(false);
    emit(next, nationalNumber);
  }

  function handleNationalChange(next: string) {
    const cleaned = next.replace(/[^\d\s/()-]/g, "");
    setNationalNumber(cleaned);
    emit(country, cleaned);
  }

  return (
    <div ref={wrapperRef} className={clsx("relative mt-1", className)}>
      <div className="flex min-w-0 gap-2">
        <div className="relative shrink-0">
          <button
            type="button"
            className="bk-input flex h-full min-h-[2.75rem] items-center gap-1.5 px-2.5 text-sm"
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            aria-controls={listId}
            onClick={() => setPickerOpen((open) => !open)}
          >
            <span className="text-lg leading-none" aria-hidden>
              {country.flag}
            </span>
            <span className="font-medium text-slate-700">{country.dialCode}</span>
            <span className="text-slate-400" aria-hidden>
              ▾
            </span>
            <span className="sr-only">{country.name}</span>
          </button>
          {pickerOpen ? (
            <ul
              id={listId}
              role="listbox"
              className="absolute left-0 top-full z-30 mt-1 max-h-56 w-64 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            >
              {sortedCountries.map((item) => (
                <li key={item.code} role="option" aria-selected={item.code === country.code}>
                  <button
                    type="button"
                    className={clsx(
                      "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50",
                      item.code === country.code && "bg-slate-50 font-medium",
                    )}
                    onClick={() => selectCountry(item)}
                  >
                    <span className="text-lg leading-none" aria-hidden>
                      {item.flag}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                    <span className="shrink-0 text-slate-500">{item.dialCode}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <input
          type="tel"
          className={clsx("bk-input min-w-0 flex-1", missing && "bk-input--missing")}
          value={nationalNumber}
          onChange={(e) => handleNationalChange(e.target.value)}
          autoComplete={autoComplete}
          required={required}
          aria-invalid={missing || undefined}
          inputMode="tel"
          placeholder="Telefonnummer"
        />
      </div>
    </div>
  );
}
